/**
 * Browser-local timezone helpers for time-of-day displays.
 *
 * Backend stores everything in UTC (Yabbit CDRs already arrive in UTC).
 * The browser knows the user's actual timezone — we use it so a Brisbane
 * user sees Brisbane hours, a Sydney user sees Sydney hours, etc.
 */

/**
 * Get the user's browser timezone abbreviation (e.g. "AEST", "AEDT", "GMT").
 *
 * We force the en-AU locale because the default ('undefined' → system) often
 * returns the less-recognisable "GMT+10" for Australian timezones. en-AU
 * uses the proper IANA short names (AEST/AEDT/AWST/ACST/ACDT) which match
 * what users actually see on their phones/calendars. For non-Australian
 * timezones en-AU still falls back to a sensible "GMT+N" string.
 */
export const getLocalTzAbbr = () => {
  try {
    const parts = new Intl.DateTimeFormat('en-AU', {
      timeZoneName: 'short',
    }).formatToParts(new Date());
    const tz = parts.find((p) => p.type === 'timeZoneName');
    if (tz?.value) return tz.value;
  } catch (e) {
    console.debug('getLocalTzAbbr: Intl.DateTimeFormat unavailable, falling through', e?.message);
    // Fallthrough
  }
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch (e) {
    console.debug('getLocalTzAbbr: resolvedOptions unavailable, returning literal "local"', e?.message);
    return 'local';
  }
};

/**
 * Convert a UTC-hour-of-day integer (0–23) into the user's local
 * hour-of-day integer (0–23). Uses today's date as the anchor so DST is
 * respected.
 */
export const utcHourToLocalHour = (utcHour) => {
  if (utcHour == null || Number.isNaN(Number(utcHour))) return null;
  // Build "today at HH:00 UTC" then read the local hour off it.
  const now = new Date();
  const d = new Date(Date.UTC(
    now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate(),
    Number(utcHour), 0, 0, 0,
  ));
  return d.getHours();
};

/**
 * Format a UTC-hour-of-day integer as "HH:00 TZ" in the user's local timezone.
 * e.g. utcHour=8 in Brisbane (UTC+10) → "18:00 AEST".
 */
export const formatUtcHourLocal = (utcHour) => {
  const localHour = utcHourToLocalHour(utcHour);
  if (localHour == null) return '—';
  return `${String(localHour).padStart(2, '0')}:00 ${getLocalTzAbbr()}`;
};
